import moment from "moment";
import { Cell } from "react-table";
import { ConnectedInfo } from "src/api/question/question-type";
import { WorkOrderListModel, WorkOrderStatus } from "src/api/work-order/workorder-types";
import { BaseTooltip } from "src/components";
import BaseNewTabLink from "src/components/BaseNewTabLink";
import DetailLink from "src/components/DetailLink";
import { PagePath } from "src/pages/product/details";
import { workOrderStatus } from "src/pages/workOrder/workOrder-types";
import { numberToStringWithComma, onChangeKoreanDays, ViewYmdFormat, YmdFormat } from "src/utils";

const WorkOrderListColumn = [
  {
    Header: "업무 번호",
    accessor: "workOrderSheetNo",
    width: 160,
    Cell: ({ row, value }: Cell<WorkOrderListModel>) => {
      const detailPath = `${PagePath.workOrder.authenticated.detail.replace(":id", String(row.original.id))}?worksheetNo=${row.original.workOrderSheetNo
        }`;
      return (
        <DetailLink to={detailPath} className="text-underline">
          {`${value.slice(0, 8)}-${value.slice(8, 13)}`}
        </DetailLink>
      );
    },
    excelValue: (row: WorkOrderListModel) => {
      return row.workOrderSheetNo ? `${row.workOrderSheetNo.slice(0, 8)}-${row.workOrderSheetNo.slice(8, 13)}` : "-";
    },
  },
  {
    Header: "업무유형",
    accessor: "workOrderSheetGroupId",
    width: 80,
    Cell: ({ value }: Cell<WorkOrderListModel>) => {
      return <p className="w-100 text-center">{value ? "정기" : "비정기"}</p>;
    },
    excelValue: (row: WorkOrderListModel) => {
      return row.workOrderSheetGroupId ? "정기" : "비정기";
    },
  },
  {
    Header: "상태",
    accessor: "status",
    width: 80,
    Cell: ({ value }: Cell<WorkOrderListModel>) => {
      let status = workOrderStatus.find((option) => option.value === value)?.label;
      let color = workOrderStatus.find((option) => option.value === value)?.tagColor;
      return <div className="w-100 flex-center-center"><span className={color}>{status}</span></div>;
    },
    excelValue: (row: WorkOrderListModel) => {
      let status = "";
      switch (row.status) {
        case WorkOrderStatus.OPEN:
          status = "접수";
          break;
        case WorkOrderStatus.CLOSED:
          status = "종료";
          break;
        case WorkOrderStatus.PROGRESS:
          status = "처리중";
          break;
        case WorkOrderStatus.COMPLETED:
          status = "처리 완료";
          break;
        case WorkOrderStatus.WAITING:
          status = "확인";
          break;
        case WorkOrderStatus.CANCELED:
          status = "취소";
          break;
        default:
          status = "-";
      }
      return status;
    },
  },
  {
    Header: "지연 여부",
    accessor: "delay",
    width: 80,
    Cell: ({ row }: Cell<WorkOrderListModel>) => {
      const status = row.original.status;
      const isOverdue = row.original.isOverdue; // 종료일자 < 오늘날짜 여부 boolean

      if (status === WorkOrderStatus.OPEN || status === WorkOrderStatus.WAITING || status === WorkOrderStatus.PROGRESS) {
        return <div className="w-100 flex-center-center"><span className={isOverdue ? "status G" : ""}>{isOverdue ? "지연" : "-"}</span></div>;
      } else {
        return <div className="w-100 text-center"><span>-</span></div>;
      }
    },
    excelValue: (row: WorkOrderListModel) => {
      const isDelay =
        (row.status === WorkOrderStatus.OPEN || row.status === WorkOrderStatus.WAITING || row.status === WorkOrderStatus.PROGRESS) && row.isOverdue;
      return isDelay ? "지연" : "-";
    },
  },
  {
    Header: "제목",
    accessor: "summary",
    width: 160,
    Cell: ({ value }: Cell<WorkOrderListModel>) => {
      return <BaseTooltip contents={value} className="w-100 d-flex justfy-content-start" />;
    },
    excelValue: (row: WorkOrderListModel) => {
      return row.summary || "-";
    },
  },



  {
    Header: "작업자",
    accessor: "mgmtOfficerName",
    width: 120,
    Cell: ({ value }: Cell<WorkOrderListModel>) => (
      <div className="w-100 d-flex justfy-content-start">
        <span>{value}</span>
      </div>
    ),
    excelValue: (row: WorkOrderListModel) => {
      return row.mgmtOfficerName || "-";
    },
  },
  {
    Header: "업무 시작일",
    accessor: "startDate",
    width: 120,
    Cell: ({ value }: Cell<WorkOrderListModel>) => {
      return (
        <div>
          <span>{value && moment(value).format(ViewYmdFormat.YYYY_MM_DD)}</span>
        </div>
      );
    },
    excelValue: (row: WorkOrderListModel) => {
      return row.startDate ? `${moment(row.startDate).format(ViewYmdFormat.YYYY_MM_DD)}` : "-";
    },
  },
  {
    Header: "처리기한",
    accessor: "endDate",
    width: 120,
    Cell: ({ value }: Cell<WorkOrderListModel>) => {
      return (
        <div>
          <span>{value && moment(value).format(ViewYmdFormat.YYYY_MM_DD)}</span>
        </div>
      );
    },
    excelValue: (row: WorkOrderListModel) => {
      return row.endDate ? `${moment(row.endDate).format(ViewYmdFormat.YYYY_MM_DD)}` : "-";
    },
  },
  {
    Header: "처리비용(원)",
    accessor: "costSum",
    width: 130,
    Cell: ({ value, row }: Cell<WorkOrderListModel>) => {
      const costCount = row.original.costCount;
      const costSum = numberToStringWithComma(value);
      const costText = `${Number(costCount) > 1 ? `(${costCount}건)` : ""} ${costSum !== "0" ? `${costSum}` : "-"}` || "-";
      return (
        <div className="d-flex justify-contents-end w-100">
          <BaseTooltip contents={costText} isSingleLine className="pr5 text-right w-100" />
        </div>
      );
    },
    excelValue: (row: WorkOrderListModel) => {
      const costCount = Number(row.costCount);
      const costSum = numberToStringWithComma(Number(row.costSum));
      return `${costCount > 1 ? `(${costCount}건)` : ""} ${costSum !== "0" ? costSum : "-"}` || "-";
    },
  },
  {
    Header: () => {
      return <span className="no-wrap">연결된 CS</span>;
    },
    accessor: "connectedInfoList",
    width: 120,
    Cell: ({ row, value }: Cell<WorkOrderListModel>) => {
      // questionId 중복 제거 후 개수 계산
      const uniqueQuestionIds = new Set(value?.map((item: ConnectedInfo) => item.questionId) || []);
      const count = uniqueQuestionIds.size;
      const detailPath = `${PagePath.workOrder.authenticated.detail.replace(":id", `${row.original.id}`)}#연결%20정보`;
      return <BaseNewTabLink path={detailPath} value={count === 0 ? "미연결" : `연결됨(${count})`}></BaseNewTabLink>;
    },
    excelValue: (row: WorkOrderListModel) => {
      // questionId 중복 제거 후 개수 계산
      const uniqueQuestionIds = new Set(row.connectedInfoList?.map((item: ConnectedInfo) => item.questionId) || []);
      const count = uniqueQuestionIds.size;
      return count === 0 ? "미연결" : `연결됨(${count})`;
    },
  },
  {
    Header: "최종 수정일시",
    accessor: "modifiedDate",
    width: 160,
    Cell: ({ value }: Cell<WorkOrderListModel>) => {
      return <p className="text-center w-100">{value && moment(value).format(YmdFormat.FULL)}</p>;
    },
    excelValue: (row: WorkOrderListModel) => {
      return row.modifiedDate ? moment(row.modifiedDate).format(YmdFormat.FULL) : "-";
    },
  },

];

export default WorkOrderListColumn;
